import Image from "next/image";

/**
 * Server-compatible skeleton for the before/after carousel.
 *
 * Renders the INITIAL STATE of the first carousel slide: a centered card with:
 * - BEFORE image full width (bottom layer)
 * - AFTER image clipped to left 50% (top layer)
 * - Comparison bar centered at left: 50%
 *
 * Mobile/tablet (≤639px): flex centering with Tailwind responsive classes.
 * Desktop (≥640px): absolute + translate centering with inline style dimensions.
 *   This guarantees exact 248×418 card size regardless of panel height or flex stretching.
 */
export function CarouselSkeleton() {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-transparent py-3 sm:py-3.5 max-[466px]:h-[calc(min(66vw,400px)+1.5rem)] min-[467px]:max-[639px]:h-[min(64vw,800px)] min-[640px]:h-[508px]">
      {/* Gradient fade edges — same as carousel's WebkitMaskImage */}
      <div
        style={{
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
          maskImage:
            "linear-gradient(to right, transparent, black 12%, black 88%, transparent)",
        }}
      >
        {/* Track — flex container, centered like carousel (mobile/tablet only) */}
        <div className="flex h-full items-center justify-center">
          <div
            className="relative shrink-0 overflow-hidden rounded-2xl border border-zinc-700/55 bg-zinc-900 shadow-md shadow-black/20 max-[466px]:w-[250px] max-[466px]:h-[min(66vw,400px)] min-[467px]:max-[639px]:w-[250px] min-[467px]:max-[639px]:h-[min(60vw,900px)] min-[640px]:hidden"
          >
            <Image
              src="/examples/carousel/agus/before/frontal_serious_current.webp"
              alt="Cargando..."
              priority
              fill
              sizes="250px"
              className="object-cover"
            />
            <div
              className="absolute inset-0 z-1"
              style={{ clipPath: "inset(0 50% 0 0)" }}
              aria-hidden
            >
              <Image
                src="/examples/carousel/agus/after/vU00_U_jAxUtRjP86nnH9_lkBTuN4n.webp"
                alt=""
                fill
                priority
                fetchPriority="high"
                sizes="250px"
                className="object-cover"
              />
            </div>
          </div>
        </div>
      </div>
      {/*
        Desktop card: at panel level (outside mask wrapper).
        Absolute + translate centering — independent of panel height.
        Inline style guarantees exact 248×418, no flex stretching.
      */}
      <div
        className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-zinc-700/55 bg-zinc-900 shadow-md shadow-black/20 max-[639px]:hidden"
        style={{ width: 248, height: 418 }}
      >
        <Image
          src="/examples/carousel/agus/before/frontal_serious_current.webp"
          alt="Cargando..."
          priority
          fill
          sizes="248px"
          className="object-cover"
        />
        <div
          className="absolute inset-0 z-1"
          style={{ clipPath: "inset(0 50% 0 0)" }}
          aria-hidden
        >
          <Image
            src="/examples/carousel/agus/after/vU00_U_jAxUtRjP86nnH9_lkBTuN4n.webp"
            alt=""
            fill
            priority
            fetchPriority="high"
            sizes="248px"
            className="object-cover"
          />
        </div>
      </div>
      {/* Centered comparison bar — matches carousel's bar styling */}
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-cyan-400/40 shadow-[0_0_12px_2px_rgba(34,211,238,0.3)]" />
    </div>
  );
}
